/*
 * fn: getTrendingCardsOverTimeHelpers
 *
 * Gets helpers for the Trending Cards Over Time graph
 *
 */
import { type ComponentProps } from 'react'
import { type XAxis, type YAxis } from 'recharts'

import { type Dict } from '@/defs/misc'
import { type PageType } from '@/defs/page'
import { type RecType } from '@/defs/rec'
import { getCardRef } from '@/utils/cardUtil'
import { formatDateFromDaysSinceEpoch, formatSigFig, getDaysSinceEpoch } from '@/utils/formatUtil'

type Props = {
  trendingCardsOverTime?: NonNullable<PageType['home']>['trendingCardsOverTime']
}

export type ChartLineType = {
  number: number
  rec: RecType
  value: string
}

type PointType = Dict<number> & { x: number }

export const getTrendingCardsOverTimeHelpers = ({ trendingCardsOverTime }: Props) => {
  const recs: RecType[] = []
  const pointsX = new Set<number>()
  const pointsY = new Set<number>()
  const pointsMap = new Map<number, Dict<number>>()

  trendingCardsOverTime?.forEach(({ name, series, slug }) => {
    if (!slug || !name) return
    if (name && slug) recs.push(getCardRef({ name, slug }))
    Object.entries(series || {}).forEach(([key, value]) => {
      const x = getDaysSinceEpoch(key)
      const y = value || 0
      pointsX.add(x)
      pointsY.add(y)
      pointsMap.set(x, { ...pointsMap.get(x), [name]: y })
    })
  })
  const points: PointType[] = [...pointsMap.entries()]
    .sort(([a], [b]) => a - b)
    .map(([x, values]) => ({ x, ...values }))

  const formatDate = (value: number) => formatDateFromDaysSinceEpoch(value, 'MMM d') || String(value)
  const formatPercent = (value: number) => `${value > 0 ? '+' : ''}${formatSigFig(value)}%`

  const minX = Math.min(...pointsX, Number.MAX_SAFE_INTEGER)
  const maxX = Math.max(...pointsX, 1)
  const ticksX = [...pointsX].sort((a, b) => a - b)

  const minY = pointsY.size > 0 ? Math.floor(Math.min(...pointsY) - 0.25) : 0
  const maxY = pointsY.size > 0 ? Math.ceil(Math.max(...pointsY) + 0.25) : 0
  const ticksY = Array.from({ length: maxY - minY + 1 }, (_, i) => minY + i)

  const xAxis: ComponentProps<typeof XAxis> = {
    dataKey: 'x',
    domain: [minX, maxX],
    niceTicks: 'none',
    tickFormatter: formatDate,
    ticks: ticksX,
  }

  const yAxis: ComponentProps<typeof YAxis> = {
    domain: [minY, maxY],
    niceTicks: 'none',
    tickFormatter: formatPercent,
    ticks: ticksY,
  }

  const pointLast = points.at(-1)
  const lines = recs
    .sort((a, b) => (pointLast?.[b.name] || 0) - (pointLast?.[a.name] || 0))
    .map<ChartLineType>((rec, i) => ({
      number: i + 1,
      rec,
      value: formatPercent(pointLast?.[rec.name] || 0),
    }))

  return { lines, points, xAxis, yAxis }
}
